import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Eye, TrendingUp, Share2 } from "lucide-react";
import api, { timeAgo } from "@/lib/api";

const Spark = ({ series }: { series: number[] }) => {
  const max = Math.max(...series, 1);
  const w = 120, h = 28;
  const pts = series.map((v, i) =>
    `${(i / (series.length - 1)) * w},${h - 2 - (v / max) * (h - 6)}`).join(" ");
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden>
      <polyline points={pts} fill="none" stroke="#00E5FF" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
};

const DEPTHS = ["25", "50", "75", "90"] as const;

const DepthFunnel = ({ funnel }: { funnel: Record<string, number> | null }) => {
  if (!funnel) return <p className="font-mono text-[10px] text-gray-600">No read-depth data yet</p>;
  return (
    <div className="flex items-end gap-2" data-testid="insight-depth-funnel">
      {DEPTHS.map((m) => (
        <div key={m} className="flex flex-col items-center gap-1">
          <span className="font-mono text-[10px] text-gray-400">{funnel[m]}%</span>
          <div className="h-8 w-5 rounded-sm bg-white/[0.05] flex items-end overflow-hidden">
            <div className="w-full bg-gradient-to-t from-[#7C3AED] to-[#00E5FF]"
              style={{ height: `${funnel[m]}%` }} />
          </div>
          <span className="font-mono text-[9px] text-gray-600">{m}%</span>
        </div>
      ))}
    </div>
  );
};

export const StoryInsights = () => {
  const { data } = useQuery({
    queryKey: ["author-insights"],
    queryFn: async () => (await api.get("/author/insights")).data,
    staleTime: 60 * 1000,
  });
  const stories = data?.stories || [];
  if (stories.length === 0) return null;
  const best = stories.reduce((a: any, b: any) => (b.views_14d > a.views_14d ? b : a), stories[0]);

  return (
    <div className="mt-6 bg-[#121212] border border-white/10 rounded-xl overflow-hidden" data-testid="story-insights-card">
      <div className="px-6 py-4 border-b border-white/[0.06] flex flex-wrap items-center justify-between gap-2">
        <span className="font-heading font-bold text-white text-sm flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-[#00E5FF]" /> Story insights
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
          Views &amp; read depth · last 14 days
        </span>
      </div>
      {best.views_14d > 0 && (
        <p className="px-6 pt-4 text-xs text-gray-400 flex items-center gap-1.5" data-testid="insight-best-performer">
          <TrendingUp className="h-3.5 w-3.5 text-[#34d399]" />
          Resonating most: <span className="text-gray-200 font-medium truncate max-w-[320px]">{best.title}</span>
          <span className="text-gray-500">· {best.views_14d} views this fortnight</span>
        </p>
      )}
      <div className="px-6 py-4 space-y-4">
        {stories.map((s: any) => (
          <div key={s.slug} data-testid={`insight-row-${s.slug}`}
            className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-white/[0.06] bg-black/30 px-4 py-3">
            <div className="min-w-0 flex-1">
              <Link to={`/article/${s.slug}`} className="text-white text-sm font-semibold hover:text-[#00E5FF] transition-colors line-clamp-1">
                {s.title}
              </Link>
              <p className="font-mono text-[10px] text-gray-500 mt-1 flex items-center gap-2">
                <span>Published {timeAgo(s.published_at)}</span>
                <span className="inline-flex items-center gap-1 text-gray-400">
                  <Eye className="h-3 w-3" /> {s.views.toLocaleString()} total views
                </span>
                {s.claps > 0 && (
                  <span className="text-gray-400" data-testid={`insight-claps-${s.slug}`}>
                    👏 {s.claps.toLocaleString()}
                  </span>
                )}
                {s.shares > 0 && (
                  <span className="inline-flex items-center gap-1 text-gray-400" data-testid={`insight-shares-${s.slug}`}>
                    <Share2 className="h-3 w-3" /> {s.shares.toLocaleString()} shares
                  </span>
                )}
              </p>
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="font-mono text-[9px] uppercase tracking-widest text-gray-600">14-day views</span>
              <Spark series={s.series} />
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="font-mono text-[9px] uppercase tracking-widest text-gray-600">Readers reaching</span>
              <DepthFunnel funnel={s.read_depth} />
            </div>
          </div>
        ))}
      </div>
      <p className="px-6 pb-4 font-mono text-[9px] text-gray-600">
        Read depth = share of tracked reads that scrolled past each point of the story.
      </p>
    </div>
  );
};
